/**
 * Страница обработки callback от Supabase Auth
 * Обрабатывает подтверждение email и сброс пароля
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase.js';

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('Обработка...');
  const [error, setError] = useState(null);

  useEffect(() => {
    async function handleCallback() {
      try {
        console.log('[AuthCallback] Начало обработки...');
        console.log('[AuthCallback] URL:', window.location.href);
        console.log('[AuthCallback] Hash:', window.location.hash);
        console.log('[AuthCallback] Search:', window.location.search);

        // Supabase может отправлять данные через hash (#) или query params (?)
        // Парсим hash если есть
        const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
        
        // Получаем параметры из URL (query или hash)
        const token = searchParams.get('token') || hashParams.get('token');
        const tokenHash = searchParams.get('token_hash') || hashParams.get('token_hash');
        const type = searchParams.get('type') || hashParams.get('type');
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const errorParam = searchParams.get('error') || hashParams.get('error');
        const errorDescription = searchParams.get('error_description') || hashParams.get('error_description');

        console.log('[AuthCallback] Параметры:', { token: !!token, tokenHash: !!tokenHash, type, accessToken: !!accessToken, error: errorParam });

        // ВАЖНО: Если это recovery (сброс пароля), сохраняем флаг СРАЗУ
        // чтобы не потерять его если Supabase обработает hash раньше
        if (type === 'recovery') {
          console.log('[AuthCallback] Обнаружен recovery flow, сохраняем флаг');
          sessionStorage.setItem('password_recovery_flow', 'true');
        }

        // Если есть ошибка в URL
        if (errorParam) {
          setError(errorDescription || errorParam);
          setStatus('Ошибка авторизации');
          setTimeout(() => navigate('/?showLogin=true'), 3000);
          return;
        }

        // Если есть access_token в hash (автоматически обрабатывается Supabase)
        if (accessToken) {
          console.log('[AuthCallback] Найден access_token, определяем тип действия...');
          
          // Пытаемся установить сессию вручную если есть refresh_token
          if (refreshToken) {
            console.log('[AuthCallback] Устанавливаем сессию из токенов...');
            const { error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            if (setSessionError) {
              console.error('[AuthCallback] Ошибка установки сессии:', setSessionError);
            }
          }
          
          // Получаем сессию
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            console.error('[AuthCallback] Ошибка получения сессии:', sessionError);
            // При recovery всё равно перенаправляем на форму
            if (type === 'recovery') {
              setStatus('Переход к установке пароля...');
              setTimeout(() => navigate('/?showResetPassword=true'), 1000);
              return;
            }
            throw new Error('Не удалось получить сессию');
          }

          // При recovery ВСЕГДА перенаправляем на форму сброса
          if (type === 'recovery') {
            console.log('[AuthCallback] Recovery flow, перенаправляем на форму сброса');
            setStatus('Переход к установке пароля...');
            setTimeout(() => {
              navigate('/?showResetPassword=true');
            }, 1000);
            return;
          }

          if (session) {
            console.log('[AuthCallback] Сессия найдена, type:', type);
            
            if (type === 'signup' || type === 'email_change') {
              // Подтверждение email - перенаправляем на логин
              setStatus('Email подтверждён! ✅');
              setTimeout(() => {
                // Выходим из сессии (не даём автоматический вход)
                supabase.auth.signOut();
                navigate('/?showLogin=true&emailConfirmed=true');
              }, 2000);
              return;
            } else {
              // Автоматический вход
              setStatus('Вход выполнен! ✅');
              // Очищаем черновики настроек YouTube при входе через callback
              try {
                sessionStorage.removeItem('youtube_settings_draft');
                console.log('[AuthCallback] Очищены черновики YouTube настроек при входе через callback');
              } catch (e) {
                console.warn('[AuthCallback] Ошибка очистки черновиков:', e);
              }
              setTimeout(() => {
                navigate('/youtube');
              }, 1500);
              return;
            }
          }
        }

        // Если есть token или token_hash (старый формат ссылок)
        if (token || tokenHash) {
          console.log('[AuthCallback] Обработка токена через verifyOtp...');
          
          if (type === 'signup' || type === 'email') {
            setStatus('Подтверждение email...');
            
            try {
              const { error: verifyError } = await supabase.auth.verifyOtp({
                token_hash: tokenHash || token,
                type: 'signup'
              });

              if (verifyError) {
                console.error('[AuthCallback] Ошибка verifyOtp:', verifyError);
                // Продолжаем - может уже подтверждён
              }
            } catch (err) {
              console.error('[AuthCallback] Ошибка обработки токена:', err);
            }
            
            setStatus('Email подтверждён! ✅');
            setTimeout(() => {
              navigate('/?showLogin=true&emailConfirmed=true');
            }, 2000);
            return;
            
          } else if (type === 'recovery') {
            setStatus('Обработка ссылки сброса...');
            
            try {
              const { data, error: verifyError } = await supabase.auth.verifyOtp({
                token_hash: tokenHash || token,
                type: 'recovery'
              });

              if (verifyError) {
                console.error('[AuthCallback] Ошибка verifyOtp recovery:', verifyError);
                throw new Error('Ссылка истекла или недействительна. Запросите новую.');
              }

              console.log('[AuthCallback] Recovery токен обработан успешно');
            } catch (err) {
              console.error('[AuthCallback] Ошибка обработки токена сброса:', err);
              setError(err.message);
              setTimeout(() => {
                navigate('/?showForgotPassword=true');
              }, 3000);
              return;
            }
            
            setStatus('Переход к установке пароля...');
            setTimeout(() => {
              navigate('/?showResetPassword=true');
            }, 1000);
            return;
          }
        }

        // Нет токена — проверяем, может сессия уже есть (например, после OAuth)
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          console.log('[AuthCallback] Сессия найдена без токена');
          setStatus('Вход выполнен! ✅');
          // Очищаем черновики настроек YouTube при входе через callback
          try {
            sessionStorage.removeItem('youtube_settings_draft');
            console.log('[AuthCallback] Очищены черновики YouTube настроек при входе через callback');
          } catch (e) {
            console.warn('[AuthCallback] Ошибка очистки черновиков:', e);
          }
          setTimeout(() => {
            navigate('/youtube');
          }, 1500);
          return;
        }

        // Ничего не найдено — перенаправляем на главную
        console.log('[AuthCallback] Токен/сессия не найдены, перенаправляем на главную');
        setStatus('Перенаправление...');
        setTimeout(() => {
          navigate('/');
        }, 1000);
        
      } catch (err) {
        console.error('[AuthCallback] Критическая ошибка:', err);
        setError(err.message || 'Ошибка обработки авторизации');
        setStatus('Ошибка');
        setTimeout(() => {
          navigate('/?showLogin=true');
        }, 3000);
      }
    }

    handleCallback();
  }, [searchParams, navigate, location]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full text-center">
        <div className="mb-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
            {error ? (
              <span className="text-3xl">❌</span>
            ) : status.includes('✅') ? (
              <span className="text-3xl">✅</span>
            ) : (
              <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {error ? 'Ошибка' : status.includes('✅') ? 'Готово!' : 'Обработка...'}
          </h1>
          <p className="text-gray-300">
            {error || status}
          </p>
        </div>
        
        {error && (
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            Вернуться на главную
          </button>
        )}
      </div>
    </div>
  );
}
